<template>
    <div :class="containerClass" @click="onWrapperClick">
        <AppTopBar @menu-toggle="onMenuToggle" />
        <div class="layout-sidebar" @click="onSidebarClick">
            <AppMenu :model="menu" @menuitem-click="onMenuItemClick" />
        </div>

        <div class="layout-main-container">
            <!-- Session Dialog -->
            <Dialog header="Alerta" v-model:visible="displayConfirmation" :style="{width: '350px'}" :modal="true" :closeOnEscape="false" :closable="false" >
                <div class="flex align-items-center justify-content-center">
                    <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
                    <span>Su sesión ha caducado, por favor ingrese nuevamente</span>
                </div>
                <template #footer>
                    <Button label="Iniciar Sesión" icon="pi pi-check" @click="onConfirmationClicked" class="p-button-text" autofocus />
                </template>
            </Dialog>

            <!-- New Message Dialog -->
             <Announcement :message="broadcastMessage" @message-read="onBroadcastRead"></Announcement>

            <div class="layout-main">
                <TrialBar />
                <router-view />
            </div>
        </div>
        <transition name="layout-mask">
            <div class="layout-mask p-component-overlay" v-if="mobileMenuActive"></div>
        </transition>
    </div>
</template>

<script>
import AppTopBar from './AppTopbar.vue';
import AppMenu from './AppMenu.vue';
import httpRequest from './service/httpRequest';
import settings from './settings';
import EconActivity from './models/econActivity';
import Business from './models/business';
import User from './models/user';
import Announcement from './components/Announcement/Announcement.vue';
import TrialBar from './components/Trial/TrialBar.vue';
import EventBus from '@/AppEventBus';
import { hasPermission, firstAllowedRoute } from './service/permissions';
import { buildMenuFromPermissions, menuFlags } from './config/menuCatalog';

// Heartbeat del WebSocket. Sirve para dos cosas: mantener viva la presencia en
// Redis (el server refresca el TTL al recibir el ping) y detectar en el cliente
// un socket muerto que el navegador no reporta con onclose (tipico al volver de
// suspension). El intervalo debe quedar holgadamente por debajo del TTL del
// server (ClientManager.CONNECTION_TTL).
const WS_PING_INTERVAL = 30000;
const WS_MAX_MISSED_PONGS = 2;

export default {
    data() {
        return {
            layoutMode: 'static',
            staticMenuInactive: false,
            overlayMenuActive: false,
            mobileMenuActive: false,
            displayConfirmation: false,
            displayMessage: false,
            showMessage: false,
            ws: null,
            tabId: null,
            wsReconnectTimer: null,
            wsPingTimer: null,
            wsPongMissed: 0,
            broadcastMessage: null,
            // El menú se construye desde los CÓDIGOS de permiso (buildMenu) usando el
            // catálogo del frontend (config/menuCatalog.js). Arranca vacío; buildMenu lo
            // puebla en created() y en cada sync/refresh.
            menu : []
        }
    },
    watch: {
        $route() {
            this.menuActive = false;
            this.$toast.removeAllGroups();
        }
    },
    async created() {
        // Generate unique tab ID
        this.tabId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);

        // Connect WebSocket - el servidor envía sync y broadcast al conectarse
        this.connectWebSocket();

        // Fallback por header: si el WS estuvo caído, el interceptor de axios detecta un
        // X-Entitlements-Version mayor en cualquier respuesta y emite este evento.
        this._onEntitlementsHeader = () => this.refreshEntitlements();
        EventBus.on('entitlements_changed', this._onEntitlementsHeader);

        // Build menu from user settings
        this.buildMenu();
    },
    beforeUnmount() {
        this.closeWebSocket();
        if (this._onEntitlementsHeader) EventBus.off('entitlements_changed', this._onEntitlementsHeader);
    },
    methods: {
        connectWebSocket() {
            this.closeWebSocket();

            var serverUrl = (settings.env === 'PROD') ? settings.prod_serverUrl : settings.dev_serverUrl;
            var wsProtocol = serverUrl.startsWith('https') ? 'wss' : 'ws';
            var host = serverUrl.replace(/^https?:\/\//, '');
            var wsUrl = wsProtocol + '://' + host + '/ws/client?tab_id=' + this.tabId + '&version=' + settings.version;

            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = function() {
                this.startHeartbeat();
            }.bind(this);

            this.ws.onmessage = function(event) {
                try {
                    var data = JSON.parse(event.data);

                    // Verificar versión del backend
                    if (data.version && data.version !== settings.version) {
                        this.closeWebSocket();
                        window.location.reload();
                        return;
                    }

                    if (data.type === 'pong') {
                        this.wsPongMissed = 0;
                        return;
                    }

                    if (data.type === 'sync') {
                        this.handleSyncMessage(data.data);
                    } else if (data.type === 'entitlements_changed') {
                        // Fase 6: los entitlements del negocio cambiaron (plan, override, rol o
                        // asiento). Refrescamos la sesión autoritativa y reconstruimos el menú
                        // SIN cerrar sesión. Sincronizamos la versión vista para que el fallback
                        // por header (X-Entitlements-Version) no dispare un segundo refresh.
                        var ev = data.data && data.data.entitlementsVersion;
                        if (ev !== undefined) httpRequest.noteEntitlementsVersion(ev);
                        this.refreshEntitlements();
                    } else if (data.type === 'screen_profiles_changed') {
                        // Fase 6: un admin editó un ScreenProfile aplicable a este negocio.
                        // Invalidamos el cache de perfiles (screenProfile.js escucha este evento);
                        // el próximo getScreenProfile trae el documento nuevo. No requiere refetch
                        // de sesión ni reconstruir el menú (los entitlements no cambiaron).
                        EventBus.emit('screen_profiles_changed');
                    } else if (data.type === 'restaurant_floor_changed') {
                        // Otro mesero (otra pestaña/dispositivo) abrió una cuenta, agregó ítems,
                        // cobró o movió una mesa: el salón se ve sin pedir F5. RestaurantFloor.vue
                        // escucha este evento y decide si le toca refrescar (filtra por actividad:
                        // presencia solo sabe de negocio, no de local).
                        EventBus.emit('restaurant_floor_changed', data.data);
                    } else if (data.type === 'session_expired') {
                        this.closeWebSocket(); // Evitar el loop de reconexión mientras no haya sesión
                        this.displayConfirmation = true;
                    } else if (data.type === 'broadcast_message') {
                        // Popup (como hoy) + lo sumamos al centro de notificaciones (campanita).
                        this.broadcastMessage = data.data;
                        this.$store.dispatch('addLiveNotification', data.data);
                    } else if (data.type === 'purchase_xml_result') {
                        // Resultado de la descarga de XML del SRI: lo reenvía al
                        // componente que lo pidió (PurchaseForm) vía event bus.
                        EventBus.emit('purchase_xml_result', data);
                    } else if (data.type === 'annulment_result') {
                        // Anular un comprobante emitido exige avisarle al SRI, y eso
                        // es un viaje por su portal que puede tardar minutos: el
                        // resultado vuelve por acá, no en la respuesta del request.
                        EventBus.emit('annulment_result', data);
                    }
                } catch (e) {
                    // Error procesando mensaje
                }
            }.bind(this);

            this.ws.onclose = function() {
                this.stopHeartbeat();
                this.wsReconnectTimer = setTimeout(function() {
                    this.connectWebSocket();
                }.bind(this), 5000);
            }.bind(this);

            this.ws.onerror = function() {
                this.ws.close();
            }.bind(this);
        },
        startHeartbeat() {
            this.stopHeartbeat();
            this.wsPongMissed = 0;

            this.wsPingTimer = setInterval(function() {
                if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

                // Sin respuesta a los ultimos pings: el socket esta muerto aunque
                // el navegador no haya disparado onclose. Reconectar a mano.
                if (this.wsPongMissed >= WS_MAX_MISSED_PONGS) {
                    this.connectWebSocket();
                    return;
                }

                try {
                    this.wsPongMissed++;
                    this.ws.send(JSON.stringify({type: 'ping'}));
                } catch (e) {
                    this.connectWebSocket();
                }
            }.bind(this), WS_PING_INTERVAL);
        },
        stopHeartbeat() {
            if (this.wsPingTimer) {
                clearInterval(this.wsPingTimer);
                this.wsPingTimer = null;
            }
        },
        closeWebSocket() {
            this.stopHeartbeat();

            if (this.wsReconnectTimer) {
                clearTimeout(this.wsReconnectTimer);
                this.wsReconnectTimer = null;
            }
            if (this.ws) {
                this.ws.onclose = null; // Evitar reconexión al cerrar intencionalmente
                this.ws.close();
                this.ws = null;
            }
        },
        handleSyncMessage(payload) {
            if (!payload) return;

            if (payload.business) {
                var business = new Business(payload.business);
                this.$store.dispatch('setBusiness', business);
            }
            if (payload.defaultActivity) {
                var activity = new EconActivity(payload.defaultActivity);
                this.$store.dispatch('setDefaultActivity', activity);
            }
            if (payload.user) {
                var user = new User(payload.user);
                this.$store.dispatch('Login', user);
            }
            if (payload.hasDevices !== undefined) {
                this.$store.dispatch('setDevices', payload.hasDevices);
            }
            // Siempre se reemplazan: una actividad sin cuentas manda la lista vacía y no
            // deben quedar las de la actividad anterior (H36).
            this.$store.dispatch('setFinantialAccounts', payload.accounts || []);
            if (payload.subscription) {
                this.$store.dispatch('setSubscription', payload.subscription);
            }

            this.buildMenu();
        },
        async refreshEntitlements() {
            // Re-fetchea la sesión (permisos, accesos y menú ya derivados por el backend) y
            // reconstruye el menú, sin cerrar sesión. Best-effort: si falla, el próximo `sync`
            // (reconexión del socket) o refresh lo aplicará.
            try {
                const session = await httpRequest.getSession();
                if (session && session.status === 200) {
                    this.handleSyncMessage(session.data);
                    this.enforceCurrentRoutePermission();
                }
            } catch (e) {
                // se aplicará en el próximo sync/refresh
            }
        },
        enforceCurrentRoutePermission() {
            // Si el usuario está parado en una ruta cuyo permiso acaba de perder, lo movemos a su
            // primer destino permitido. (El API ya queda blindado por el decorador del backend;
            // esto es solo para que la pantalla abierta no quede colgada tras un revoke.)
            const route = this.$route;
            const code = route && route.meta && route.meta.permission;
            if (!code || hasPermission(code)) return;
            const landing = firstAllowedRoute();
            if (this.$route.path !== landing) this.$router.replace(landing);
        },
        async onBroadcastRead(messageId) {
            // Cerrar el popup solo lo marca "visto" (seen global por negocio) para que no vuelva a
            // saltar. NO lo marca "leído" en la campanita: el broadcast queda como NO leído en el
            // centro de notificaciones (inbox persistente) hasta que el usuario lo abra ahí. Así la
            // campanita refleja de verdad los mensajes recibidos (badge + resaltado), en vez de
            // apagarse apenas se cierra el popup.
            var response = await httpRequest.seenBroadcastMessage(messageId);
            if (response.status === 200) {
                this.broadcastMessage = null;
            }
        },
        buildMenu() {
            // El menú se DERIVA de los códigos de permiso del usuario (settings.permissions),
            // filtrando el catálogo del frontend (config/menuCatalog.js). Ya NO se lee el menú
            // pre-armado por el backend (settings.accesses, legacy) para renderizar — así el
            // menú es coherente con el gating de rutas (ambos por código) y un operador ve
            // exactamente sus ítems sin depender de que el backend arme un `accesses` correcto.
            const userSettings = (this.$store.getters.getUser.settings) || {};

            // Menú desde CÓDIGOS (permiso = visibilidad). Contabilidad va en el catálogo gated por
            // code 'accounting' + flag `accountingActive` (módulo habilitado en la actividad).
            // Ya NO se usa settings_headers para ocultar (su default per-usuario ocultaba "Cuentas"
            // pese a tener el permiso); ni se adjunta Contabilidad desde `accesses` (3b.2c resuelto).
            // Menú congelado (`settings.menu`, §2.6): los usuarios que ya existían siguen viendo
            // exactamente lo que veían; cada pantalla nueva se habilita desde la consola de superadmin.
            let menu = buildMenuFromPermissions(hasPermission, {
                flags: menuFlags(userSettings),
                visible: userSettings.menu,
            });

            // La consola de superadmin vive en su propio sitio (repo contablusSuperadmin,
            // admin.contablus.com): el menú del dashboard ya no la inyecta.
            this.menu = menu;
        },
        async onConfirmationClicked() {
            await this.$store.dispatch('Logout');
            this.$router.push('/login');
        },
        async syncAccount() {
            const response = await httpRequest.syncAccount();
            if (response.status === 200) {
                // Store business
                let business = new Business(response.data.business);
                await this.$store.dispatch('setBusiness', business);

                // Store default activity
                let activity = new EconActivity(response.data.defaultActivity);
                await this.$store.dispatch('setDefaultActivity', activity);

                // Store user role
                let user = new User(response.data.user);
                await this.$store.dispatch('Login', user);
                
                // Store devices
                let hasDevices = response.data.hasDevices;
                await this.$store.dispatch('setDevices', hasDevices);

                // Store finantial accounts
                let finantialAccounts = response.data.accounts || [];
                await this.$store.dispatch('setFinantialAccounts', finantialAccounts);

                // Store subscription (barra/modal de prueba)
                if (response.data.subscription) {
                    await this.$store.dispatch('setSubscription', response.data.subscription);
                }

                // Rebuild menu with updated user data
                this.buildMenu();

            }  else if (response.status == 502) { // Unauthorized
                this.displayConfirmation = true;             
            }
        },
        onWrapperClick() {
            if (!this.menuClick) {
                this.overlayMenuActive = false;
                this.mobileMenuActive = false;
            }

            this.menuClick = false;
        },
        onMenuToggle() {
            this.menuClick = true;

            if (this.isDesktop()) {
                if (this.layoutMode === 'overlay') {
					if(this.mobileMenuActive === true) {
						this.overlayMenuActive = true;
					}

                    this.overlayMenuActive = !this.overlayMenuActive;
					this.mobileMenuActive = false;
                }
                else if (this.layoutMode === 'static') {
                    this.staticMenuInactive = !this.staticMenuInactive;
                }
            }
            else {
                this.mobileMenuActive = !this.mobileMenuActive;
            }
        },
        onSidebarClick() {
            this.menuClick = true;
        },
        onMenuItemClick(event) {
            if (event.item && !event.item.items) {
                this.overlayMenuActive = false;
                this.mobileMenuActive = false;
            }
        },
		onLayoutChange(layoutMode) {
			this.layoutMode = layoutMode;
		},
        addClass(element, className) {
            if (element.classList)
                element.classList.add(className);
            else
                element.className += ' ' + className;
        },
        removeClass(element, className) {
            if (element.classList)
                element.classList.remove(className);
            else
                element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        },
        isDesktop() {
            return window.innerWidth >= 992;
        },
        isSidebarVisible() {
            if (this.isDesktop()) {
                if (this.layoutMode === 'static')
                    return !this.staticMenuInactive;
                else if (this.layoutMode === 'overlay')
                    return this.overlayMenuActive;
            }

            return true;
        }
    },
    computed: {
        containerClass() {
            return ['layout-wrapper', {
                'layout-overlay': this.layoutMode === 'overlay',
                'layout-static': this.layoutMode === 'static',
                'layout-static-sidebar-inactive': this.staticMenuInactive && this.layoutMode === 'static',
                'layout-overlay-sidebar-active': this.overlayMenuActive && this.layoutMode === 'overlay',
                'layout-mobile-sidebar-active': this.mobileMenuActive,
				'p-input-filled': this.$primevue.config.inputStyle === 'filled',
				'p-ripple-disabled': this.$primevue.config.ripple === false
            }];
        },
        logo() {
            return (this.$appState.darkTheme) ? "images/logo-white.svg" : "images/logo.svg";
        }
    },
    beforeUpdate() {
        if (this.mobileMenuActive)
            this.addClass(document.body, 'body-overflow-hidden');
        else
            this.removeClass(document.body, 'body-overflow-hidden');
    },
    components: {
        'AppTopBar': AppTopBar,
        'AppMenu': AppMenu,
        'Announcement': Announcement,
        'TrialBar': TrialBar,
    }
}
</script>
